import { memo, useEffect, useState } from "react";
import Board from "@publicPortal/components/board/Board";
import ResponsiveAppBar from "@publicPortal/components/responsiveAppBar/ResponsiveAppBar";
import Grid from "@mui/material/Grid";
import PlanningAppealsService from "@publicPortal/services/PlanningAppealsService";
import { useParams } from "react-router";
import { BackButton } from "@publicPortal/components/BackButton/BackButton";
import { IPlanningAppealDetails } from "@shared/publicportal/contracts/IPlanningAppealDetails";
import Field from "@mirage/components/regularField/Field";
import { TextNavigate } from "@mirage/components/buttons/TextNavigate/TextNavigate";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";

const routing = new PublicPortalRoutingService();

const PlanningAppeals = memo(() => {
    const [appeal, setAppeal] = useState<IPlanningAppealDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const { id } = useParams();

    useEffect(() => {
        const getPlanningAppeal = async () => {
            setLoading(true);
            const service = new PlanningAppealsService();
            if (!id || isNaN(parseInt(id))) {
                setLoading(false);
                setAppeal(null);
            } else {
                const appeal = await service.getPlanningAppealById(Number(id));
                setLoading(false);
                setAppeal(appeal);
            }
        };
        getPlanningAppeal();
    }, [id]);
    const navigateToApplication = appeal?.planningApplication?.id ? routing.PlanningApplicationRecordRoute(appeal?.planningApplication?.id) : null;
    const applicationExists = appeal?.planningApplication?.id && !isNaN(appeal?.planningApplication?.id);
    const applicationNameValue = applicationExists ? <TextNavigate navigateTo={navigateToApplication}>{appeal?.planningApplication?.name || "(none)"}</TextNavigate> : "(none)";

    if (!appeal && !loading) {
        return (
            <>
                <ResponsiveAppBar />
                <Board headerText="Planning Appeal not found" loadingHeaderText={loading} size="big">
                    <BackButton />
                </Board>
            </>
        );
    }

    return (
        <>
            <ResponsiveAppBar />
            <Board headerText={"Planning Appeal " + (appeal?.name || "(none)")} loadingHeaderText={loading} size="big">
                <BackButton />
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6, lg: 6, xl: 6 }}>
                        <Grid>
                            <Field label="Appeal Ref No." mode="view" value={appeal?.name} contentType="TEXT" hideOnEmpty />
                        </Grid>
                        <Grid>
                            <Field label="Appeal Description" mode="view" value={appeal?.appealDescription} contentType="TEXT" hideOnEmpty />
                        </Grid>
                        <Grid>
                            <Field label="Address" mode="view" value={appeal?.planningApplication?.location} contentType="TEXT" hideOnEmpty />
                        </Grid>
                        <Grid>
                            <Field label="Planning Application" mode="view" value={applicationNameValue} contentType="TEXT" hideOnEmpty />
                        </Grid>
                        <Grid>
                            <Field label="Appeal Type" mode="view" value={appeal?.appealType} contentType="TEXT" hideOnEmpty />
                        </Grid>
                        <Grid>
                            <Field label="Appeal Level" mode="view" value={appeal?.appealLevel} contentType="TEXT" hideOnEmpty />
                        </Grid>
                        <Grid>
                            <Field label="Planning Inspectorate Ref" mode="view" value={appeal?.planningInspectorateRef} contentType="TEXT" hideOnEmpty />
                        </Grid>
                        <Grid>
                            <Field label="Appellant Type" mode="view" value={appeal?.apellantType} contentType="TEXT" hideOnEmpty />
                        </Grid>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6, lg: 6, xl: 6 }} className="grid-nextItem">
                        <Grid>
                            <Field label="Case Officer" mode="view" value={appeal?.officer?.name} contentType="TEXT" hideOnEmpty />
                        </Grid>
                        <Grid>
                            <Field label="Planning Inspector" mode="view" value={appeal?.planningInspector} contentType="TEXT" hideOnEmpty />
                        </Grid>
                        <Grid>
                            <Field label="Date Lodged" mode="view" value={appeal?.dateLodged} contentType="DATE" hideOnEmpty />
                        </Grid>
                        <Grid>
                            <Field label="Date Started" mode="view" value={appeal?.dateStarted} contentType="DATE" hideOnEmpty />
                        </Grid>
                        <Grid>
                            <Field label="Appeal Decision" mode="view" value={appeal?.decisionType?.name} contentType="TEXT" hideOnEmpty />
                        </Grid>
                        <Grid>
                            <Field label="Appeal Decision Date" mode="view" value={appeal?.appealDecisionDate} contentType="DATE" hideOnEmpty />
                        </Grid>
                    </Grid>
                </Grid>
            </Board>
        </>
    );
});

export default PlanningAppeals;
